import { createForm, ifBoolParse } from 'heliocor-ui'
import isEmpty from 'lodash/isEmpty'


import { phaseTypes } from '../../config/phaseTypes.enum'
import { store } from '../../App'
import { Authentication } from '../../pages/Authentication'
import { Documents } from '../../pages/Documents'
import { Structure } from '../../pages/Structure'
import { Summary } from '../../pages/Summary'
import { isCategoryConditionSatisfied, isKycTypeConditionSatisfied } from '../structures/helpers'
import { KYCSelection } from '../../pages/KYCSelection'
import { YapilyStep } from '../../pages/YapilyStep'
import { getParsedDependencies } from '../../utils/parsers'


export const StepsGenerator = ({ 
    isLoading = false,
    inputChangedHandler,
    errorHandler,
    updateNavigationButtons,
    documentsRequest = [],
    cleanValues,
    customFields,
    showCategories,
    showFields,
    showStructure,
    country,
    supportedRegions,
    includeStructure,
    categories,
    values,
    t,
    showKYCTypeStep,
    showYapilyStep,
    errors
}) => {

    const phase = store.getState().config.phase
    const kycType = store.getState().settings.kycType
    const isRequest = store.getState().config.isRequest
    const partyType = store.getState().config.partyType?.toLowerCase()
    const KYCSettings = store.getState().settings[`${partyType}KYCSettings`] || {}
    const authenticated = store.getState().config.authenticated


    const verificationForm = {
        id: 'authentication',
        component: Authentication,
    }

    const kycSelection = authenticated && {
        id: KYCSettings.KYCStepName || 'kycSelection',
        component: KYCSelection,
        props: { errorHandler, settings: KYCSettings }
    }

    const summary = {
        id: 'summary',
        component: Summary,
        props: { id: 'summary', documentsRequest }
    }

    const companyDetails = {
        id: "companyDetails",
        component: Summary,
        props: { id: 'companyDetails', documentsRequest }
    }

    const personDetails = {
        id: "personDetails",
        component: Summary,
        props: { id: 'personDetails', documentsRequest }
    }

    const hasStructure = (phase === phaseTypes.REQUEST && showStructure)
        || (phase === phaseTypes.PREAPPROVAL && includeStructure)

    const structure = hasStructure && {   
        id: 'STRUCTURE', 
        component: Structure,
        props: { updateNavigationButtons }
    }

    const uploadDocumentsArray = documentsRequest
        .filter(cat => {
            const dependencies = getParsedDependencies(cat.dependencies)
            if (!isEmpty(dependencies)) {
                const valueToCheck = values[dependencies.field]?.value || values[dependencies.field]
                const isArray = Array.isArray(valueToCheck)
                const isString = typeof valueToCheck === 'string'

                return isArray
                    ? valueToCheck.some(val => dependencies.values.includes(val))
                    : dependencies.values.includes(isString ? valueToCheck : valueToCheck?.toString())
            } else {
                return true
            }
        })
        .map(cat => {
            return ({
                ...cat,
                documents: cat.documents.filter(doc => {
                    let ret = true
                    const dependencies = getParsedDependencies(doc.dependencies)
                    const value = values[dependencies?.field]?.value || values[dependencies?.field]

                    if (dependencies) {
                        if (dependencies.type === 'BOOLEAN') {
                            ret = value && value === ifBoolParse(dependencies.values[0])
                        } else if (dependencies.type === 'SELECT') {
                            ret = Array.isArray(value) 
                                ? value?.some(val => dependencies.values.includes(val)) 
                                : dependencies.values.includes(value)
                        }
                    }
                    return ret
                })
            })
        })
        .map(cat => ({
            id: cat.code,
            title: cat.name,
            type: 'documents',
            component: Documents,
            props: { category: cat, errorHandler }
        })
    )

    const yapilyStep = {
        id: 'Bank Account Validation',
        component: YapilyStep,
        props: { errorHandler, errors }
    }
    
    const mapFieldToFormFieldType = field => {
        const _field = {...field, customLabel: field.customLabel || t(field.label)}
        switch (field.type) {
            case 'SELECT':
                return {..._field, closeMenuOnScroll: (e) => e?.target?.id === 'wizard-container'}
            default:
                return _field
        }
    }

    const isCategoryVisible = cat => {
        const categoryDetails = categories.find(c => (c.code === cat || c.name === cat))
        const dependency = categoryDetails?.dependencies || {}

        if(isEmpty(dependency)) return true

        const showCats = showCategories && JSON.parse(showCategories)
        const isVisible = isRequest ? dependency.CAFE_PHASE_2 : dependency.CAFE_PHASE_1
   
        return isVisible && (
            !showCats
            || showCats.includes(cat)
            || !isEmpty(customFields[cat])
            || categoryDetails?.description
        )
    }

    const isCategoryDependencySatisfied = cat => {
        const categoryDetails = categories.find(c => (c.code === cat || c.name === cat))
        const dependency = categoryDetails?.dependencies || {}
        return isCategoryConditionSatisfied({dependency, values, isRequest})
    }

    const partyFormsArray = Object
        .keys(customFields)
        .filter(isCategoryVisible)
        .filter(isCategoryDependencySatisfied)
        .map(cat => {
            const categoryDetails = categories.find(c => (c.code === cat || c.name === cat))
            const categoryDescription = categoryDetails?.description;

            //If present the CAFe will show only these fields
            const _showFields = showFields && JSON.parse(showFields)
            const isFieldVisible = (f) => _showFields ? _showFields.includes(f.id) : true
            const isFieldDependencySatisfied = (f) => isKycTypeConditionSatisfied({ field: f, kycType })

            if (supportedRegions) {

                if (customFields[cat].some(f => f.id === 'registrationNumber') && !!supportedRegions[country] && !customFields[cat].find(f => f.id === 'region')) {

                    const mappedFields = customFields[cat].map(f => f.id === 'registeredAddressCountry' ? 'reg' : 'No')
                    const spliceIndex = mappedFields.indexOf('reg') + 1
                
                    customFields[cat].splice(spliceIndex, 0, {
                        id: 'region',
                        type: 'SELECT',
                        customOptions: supportedRegions[country].map(u => ({ value: u, label: u.toUpperCase() })),
                        editable: true
                    })
                } else if (!supportedRegions[country] && customFields[cat].find(f => f.id === 'region')) {
                    customFields[cat] = customFields[cat].filter(f => f.id !== 'region')
                }
            }

            let _fields = customFields[cat] || []

            return {
                id: cat,
                title: cat,
                type: 'fields',
                description: categoryDescription,
                form: createForm({
                    fields: _fields
                        .filter(isFieldVisible)
                        .filter(isFieldDependencySatisfied)
                        .map(mapFieldToFormFieldType),
                    props: { inputChangedHandler, errorHandler, isLoading, cleanValues, isEdit: true, noFocus: !!categoryDescription, t }
                }),
                sendToApi: isRequest
            }
        })



    const forms = {
        [phaseTypes.REQUEST]: {
            VERIFICATION: [verificationForm],
            COMPANY: [verificationForm, companyDetails, ...partyFormsArray, ...uploadDocumentsArray, structure, summary].filter(Boolean),
            PERSON: [verificationForm, personDetails, ...partyFormsArray, ...uploadDocumentsArray, summary],
            CONNECTED_PERSON: [verificationForm, personDetails, ...partyFormsArray, ...uploadDocumentsArray, summary],
            CONNECTED_COMPANY: [verificationForm, companyDetails, ...partyFormsArray, ...uploadDocumentsArray, summary]
        },
        [phaseTypes.PREAPPROVAL]: {
            VERIFICATION: [verificationForm],
            uploadDocuments: uploadDocumentsArray,
            COMPANY: [verificationForm, ...(kycType
                ? [showKYCTypeStep && kycSelection, ...partyFormsArray, ...uploadDocumentsArray, structure, summary].filter(Boolean)
                : [showKYCTypeStep && kycSelection].filter(Boolean))
            ],
            PERSON: [verificationForm, ...(kycType
                ? [showKYCTypeStep && kycSelection, ...partyFormsArray, ...uploadDocumentsArray, showYapilyStep && yapilyStep, summary].filter(Boolean)
                : [showKYCTypeStep && kycSelection].filter(Boolean))
            ],
        },
        [phaseTypes.UNKNOWN_ORG]: {
            VERIFICATION: [verificationForm]
        }
    }

    return forms[phase] || { VERIFICATION: verificationForm }
}